import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import LiveSvgPreview from "./LiveSvgPreview";

const HANDLE_POS = [
  { corner: "tl", cls: "-left-1.5 -top-1.5 cursor-nwse-resize" },
  { corner: "tr", cls: "-right-1.5 -top-1.5 cursor-nesw-resize" },
  { corner: "bl", cls: "-left-1.5 -bottom-1.5 cursor-nesw-resize" },
  { corner: "br", cls: "-right-1.5 -bottom-1.5 cursor-nwse-resize" },
];

function snapAxis(pos, size, pageCenter, siblingStarts, siblingCenters, siblingEnds, threshold) {
  const center = pos + size / 2;
  if (Math.abs(center - pageCenter) < threshold) return { value: pageCenter - size / 2, guide: pageCenter };
  for (const c of siblingCenters) {
    if (Math.abs(center - c) < threshold) return { value: c - size / 2, guide: c };
  }
  for (const s of siblingStarts) {
    if (Math.abs(pos - s) < threshold) return { value: s, guide: s };
  }
  for (const e of siblingEnds) {
    if (Math.abs(pos + size - e) < threshold) return { value: e - size, guide: e };
  }
  return null;
}

function DragBox({ box, unitWidth, unitHeight, siblings, canvasRef, selected, onSelect, onCommit, onGuideChange, locked, spellCount = 0 }) {
  const dragRef = useRef(null);
  const resizeRef = useRef(null);
  const liveRef = useRef(null);
  const elRef = useRef(null);

  const setRectStyle = (x, y, w, h) => {
    if (!elRef.current) return;
    elRef.current.style.left = `${(x / unitWidth) * 100}%`;
    elRef.current.style.top = `${(y / unitHeight) * 100}%`;
    elRef.current.style.width = `${(w / unitWidth) * 100}%`;
    elRef.current.style.height = `${(h / unitHeight) * 100}%`;
  };

  const getContainer = () => canvasRef.current?.getBoundingClientRect();

  const handlePointerDown = (e) => {
    e.stopPropagation();
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    onSelect();
    const rect = getContainer();
    dragRef.current = { startClientX: e.clientX, startClientY: e.clientY, startX: box.x, startY: box.y, moved: false, containerWidth: rect.width, containerHeight: rect.height };
  };

  const handlePointerMove = (e) => {
    if (!dragRef.current) return;
    const dx = e.clientX - dragRef.current.startClientX;
    const dy = e.clientY - dragRef.current.startClientY;
    if (Math.abs(dx) > 2 || Math.abs(dy) > 2) dragRef.current.moved = true;
    const unitPerPxX = unitWidth / dragRef.current.containerWidth;
    const unitPerPxY = unitHeight / dragRef.current.containerHeight;
    const threshX = 8 * unitPerPxX;
    const threshY = 8 * unitPerPxY;
    let newX = Math.max(0, Math.min(unitWidth - box.width, dragRef.current.startX + dx * unitPerPxX));
    let newY = Math.max(0, Math.min(unitHeight - box.height, dragRef.current.startY + dy * unitPerPxY));

    const sibX = siblings.map((s) => s.x);
    const sibCX = siblings.map((s) => s.x + s.width / 2);
    const sibXE = siblings.map((s) => s.x + s.width);
    const sibY = siblings.map((s) => s.y);
    const sibCY = siblings.map((s) => s.y + s.height / 2);
    const sibYE = siblings.map((s) => s.y + s.height);

    const snapX = snapAxis(newX, box.width, unitWidth / 2, sibX, sibCX, sibXE, threshX);
    const snapY = snapAxis(newY, box.height, unitHeight / 2, sibY, sibCY, sibYE, threshY);
    const guides = [];
    if (snapX) { newX = snapX.value; guides.push({ axis: "v", pos: (snapX.guide / unitWidth) * 100 }); }
    if (snapY) { newY = snapY.value; guides.push({ axis: "h", pos: (snapY.guide / unitHeight) * 100 }); }
    onGuideChange(guides);

    liveRef.current = { x: newX, y: newY, width: box.width, height: box.height };
    setRectStyle(newX, newY, box.width, box.height);
  };

  const handlePointerUp = () => {
    if (dragRef.current?.moved && liveRef.current) onCommit(liveRef.current);
    dragRef.current = null;
    liveRef.current = null;
    onGuideChange([]);
  };

  const minW = unitWidth * 0.02;
  const minH = unitHeight * 0.02;

  const handleResizeDown = (corner, e) => {
    e.stopPropagation();
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    onSelect();
    const rect = getContainer();
    resizeRef.current = { corner, startClientX: e.clientX, startClientY: e.clientY, startX: box.x, startY: box.y, startW: box.width, startH: box.height, containerWidth: rect.width, containerHeight: rect.height };
  };

  const handleResizeMove = (e) => {
    if (!resizeRef.current) return;
    const r = resizeRef.current;
    const unitPerPxX = unitWidth / r.containerWidth;
    const unitPerPxY = unitHeight / r.containerHeight;
    const dx = (e.clientX - r.startClientX) * unitPerPxX;
    const dy = (e.clientY - r.startClientY) * unitPerPxY;
    let { startX: x, startY: y, startW: w, startH: h } = r;

    if (r.corner.includes("r")) w = Math.max(minW, r.startW + dx);
    if (r.corner.includes("l")) { w = Math.max(minW, r.startW - dx); x = r.startX + (r.startW - w); }
    if (r.corner.includes("b")) h = Math.max(minH, r.startH + dy);
    if (r.corner.includes("t")) { h = Math.max(minH, r.startH - dy); y = r.startY + (r.startH - h); }

    x = Math.max(0, Math.min(unitWidth - w, x));
    y = Math.max(0, Math.min(unitHeight - h, y));
    if (x + w > unitWidth) w = unitWidth - x;
    if (y + h > unitHeight) h = unitHeight - y;

    liveRef.current = { x, y, width: w, height: h };
    setRectStyle(x, y, w, h);
  };

  const handleResizeUp = () => {
    if (liveRef.current) onCommit(liveRef.current);
    resizeRef.current = null;
    liveRef.current = null;
  };

  return (
    <div
      ref={elRef}
      onPointerDown={locked ? undefined : handlePointerDown}
      onPointerMove={locked ? undefined : handlePointerMove}
      onPointerUp={locked ? undefined : handlePointerUp}
      onClick={locked ? onSelect : undefined}
      data-testid={`drag-box-${box.testId}`}
      title={box.label}
      className={`absolute border-2 transition-colors ${locked ? "cursor-pointer" : "cursor-move"} ${
        selected ? "border-indigo-500 bg-indigo-500/10 z-10" : "border-transparent hover:border-indigo-300 hover:bg-indigo-500/5"
      }`}
      style={{
        left: `${(box.x / unitWidth) * 100}%`,
        top: `${(box.y / unitHeight) * 100}%`,
        width: `${(box.width / unitWidth) * 100}%`,
        height: `${(box.height / unitHeight) * 100}%`,
      }}
    >
      {selected && !locked && HANDLE_POS.map(({ corner, cls }) => (
        <div
          key={corner}
          onPointerDown={(e) => handleResizeDown(corner, e)}
          onPointerMove={handleResizeMove}
          onPointerUp={handleResizeUp}
          data-testid={`resize-handle-${box.testId}-${corner}`}
          className={`absolute h-3 w-3 rounded-full border-2 border-white bg-indigo-500 shadow ${cls}`}
        />
      ))}
      {spellCount > 0 && (
        <>
          <div className="pointer-events-none absolute inset-x-0 bottom-0" style={{ borderBottom: "2px dashed #ef4444" }} />
          <div className="pointer-events-none absolute -right-2 -top-2 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-red-500 px-1 text-[9px] font-bold text-white shadow" data-testid={`spell-badge-${box.testId}`}>{spellCount}</div>
        </>
      )}
    </div>
  );
}

function DrawLayer({ unitWidth, unitHeight, canvasRef, onComplete }) {
  const [rect, setRect] = useState(null);
  const startRef = useRef(null);

  const handleDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const bounds = canvasRef.current.getBoundingClientRect();
    const sx = e.clientX - bounds.left;
    const sy = e.clientY - bounds.top;
    startRef.current = { sx, sy, bounds };
    setRect({ left: sx, top: sy, width: 0, height: 0 });
  };

  const handleMove = (e) => {
    if (!startRef.current) return;
    const { sx, sy, bounds } = startRef.current;
    const cx = Math.max(0, Math.min(bounds.width, e.clientX - bounds.left));
    const cy = Math.max(0, Math.min(bounds.height, e.clientY - bounds.top));
    setRect({ left: Math.min(sx, cx), top: Math.min(sy, cy), width: Math.abs(cx - sx), height: Math.abs(cy - sy) });
  };

  const handleUp = () => {
    if (!startRef.current) return;
    const { sx, sy, bounds } = startRef.current;
    const unitPerPxX = unitWidth / bounds.width;
    const unitPerPxY = unitHeight / bounds.height;
    const isClick = !rect || (rect.width < 4 && rect.height < 4);
    let x, y, width, height;
    if (isClick) {
      width = unitWidth * 0.18;
      height = unitHeight * 0.12;
      x = sx * unitPerPxX - width / 2;
      y = sy * unitPerPxY - height / 2;
    } else {
      width = rect.width * unitPerPxX;
      height = rect.height * unitPerPxY;
      x = rect.left * unitPerPxX;
      y = rect.top * unitPerPxY;
    }
    x = Math.max(0, Math.min(unitWidth - width, x));
    y = Math.max(0, Math.min(unitHeight - height, y));
    startRef.current = null;
    setRect(null);
    onComplete({ x, y, width, height });
  };

  return (
    <div
      className="absolute inset-0 z-20 cursor-crosshair bg-indigo-500/5"
      data-testid="draw-logo-layer"
      onPointerDown={handleDown}
      onPointerMove={handleMove}
      onPointerUp={handleUp}
    >
      {rect && (
        <div
          className="absolute border-2 border-dashed border-indigo-500 bg-indigo-500/15"
          data-testid="draw-logo-preview-rect"
          style={{ left: rect.left, top: rect.top, width: rect.width, height: rect.height }}
        />
      )}
    </div>
  );
}

export default function DesignerCanvas({ generating, design, zoom, mode, template, product, brand, customContent, boxes, selection, onSelectBox, onCommitBox, showSafeArea, bleedXPct, bleedYPct, drawMode, onDrawComplete, rotation = 0, spellResults = {} }) {
  const unitWidth = mode === "A" ? template?.widthMM : customContent.canvasWidth;
  const unitHeight = mode === "A" ? template?.heightMM : customContent.canvasHeight;
  const canvasRef = useRef(null);
  const [guides, setGuides] = useState([]);
  const { t } = useTranslation();

  if (generating) {
    return (
      <div className="flex flex-col items-center gap-3 text-sm text-slate-400" data-testid="generating-indicator">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-slate-200 border-t-indigo-600" />
        {t("designer.generatingCard")}
      </div>
    );
  }

  if (!design) {
    return (
      <div className="max-w-xs text-center text-sm text-slate-400" data-testid="designer-empty-state">
        Select a Product, Template and Brand above to generate a preview.
      </div>
    );
  }

  return (
    <div ref={canvasRef} className="relative shadow-xl transition-transform duration-300" data-testid="designer-canvas-root" style={{ width: `${7.2 * zoom}px`, transform: `rotate(${rotation}deg)` }}>
      <LiveSvgPreview mode={mode} product={product} template={template} brand={brand} customContent={customContent} />
      {mode === "A" && template && (
        <>
          <div className="pointer-events-none absolute inset-0 border-2 border-red-400/70" style={{ margin: `${bleedYPct}% ${bleedXPct}%` }} />
          {showSafeArea && <div className="pointer-events-none absolute inset-0 border border-dashed border-emerald-400/70" style={{ margin: `${bleedYPct * 2.4}% ${bleedXPct * 2.4}%` }} />}
        </>
      )}
      {guides.map((g, i) => (
        <div
          key={i}
          data-testid={`snap-guide-${g.axis}`}
          className="pointer-events-none absolute bg-pink-500/80"
          style={g.axis === "v" ? { left: `${g.pos}%`, top: 0, bottom: 0, width: "1px" } : { top: `${g.pos}%`, left: 0, right: 0, height: "1px" }}
        />
      ))}
      {unitWidth > 0 && unitHeight > 0 && boxes.map((box) => {
        const spellKey = box.kind === "fixed" ? `fixed:${box.key}` : box.kind === "text" ? `text:${box.key}` : box.kind === "overlay" ? `overlay:${box.key}` : null;
        const spellCount = (spellKey && spellResults[spellKey]?.issues?.length) || 0;
        return (
        <DragBox
          key={`${box.kind}-${box.key}`}
          box={box}
          unitWidth={unitWidth}
          unitHeight={unitHeight}
          siblings={boxes.filter((b) => !(b.kind === box.kind && b.key === box.key))}
          canvasRef={canvasRef}
          selected={selection && selection.kind === box.kind && selection.key === box.key}
          onSelect={() => onSelectBox(box)}
          onCommit={(patch) => onCommitBox(box, patch)}
          onGuideChange={setGuides}
          locked={rotation !== 0}
          spellCount={spellCount}
        />
        );
      })}
      {drawMode && rotation === 0 && unitWidth > 0 && unitHeight > 0 && (
        <DrawLayer unitWidth={unitWidth} unitHeight={unitHeight} canvasRef={canvasRef} onComplete={onDrawComplete} />
      )}
    </div>
  );
}
